import type {
    ComposeRibbonControlDefProps,
    ReadOnlyRibbonControlDefProps,
    RibbonControlDefinitionProps,
} from './RibbonControlDefinitionProps';
import { type MailRibbonControlId, type MailRibbonTabId } from 'owa-ribbon-ids/lib/mailRibbonId';
import type { RibbonControlId, RibbonTabId } from 'owa-ribbon-ids';
import {
    getAccountScopeUserSettings,
    getUserConfiguration,
    isConsumer,
    isPaid,
} from 'owa-session-store';
import {
    getMailMenuItemShouldHideForMailbox,
    getMailMenuItemShouldShow,
} from 'owa-mail-filterable-menu-behavior';
import { type ActionControlId } from 'owa-ribbon-ids/lib/actionControlId';
import { type FormatControlId } from 'owa-ribbon-ids/lib/formatControlId';
import type { MailboxInfo } from 'owa-client-types';
import { type MenuItemType } from 'owa-filterable-menu/lib/components/MenuItemType';
import { addinsCapability } from 'owa-capabilities-definitions/lib/addinsCapability';
import { owaComputedFn } from 'owa-computed-fn';
import { getCmdIdFromRibbonId } from 'owa-mail-ribbon-store/lib/util/mailRibbonIdToCmdIdMap';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { getRibbonMailboxInfo } from './getRibbonMailboxInfo';
import { hasEnterpriseAccount } from 'owa-mail-ribbon-store/lib/util/hasEnterpriseAccount';
import { isBrowserChrome, isBrowserEDGECHROMIUM, isBrowserFirefox } from 'owa-user-agent';
import { isCapabilityEnabled, isCapabilitySupported } from 'owa-capabilities';
import { isCopilotFeatureEnabled } from 'owa-copilot-settings-utils';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import isEncryptionTemplateAvailable from 'owa-mail-protection/lib/utils/isEncryptionTemplateAvailable';
import { isGlobalFeatureEnabled } from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import { LoopType, isFluidEnabledForSource } from 'owa-fluid-validations';
import isGroupComposeViewState from 'owa-mail-compose-store/lib/utils/isGroupComposeViewState';
import { isMOS3AppServiceAppsInMailAvailable } from 'owa-m365-acquisitions/lib/utils/isMOS3AppServiceAppsInMailAvailable';
import { isMessageExtensionsFlyoutEnabled } from 'owa-m365-acquisitions/lib/utils/isMessageExtensionsFlyoutEnabled';
import { isNativeReportingEnabled } from 'owa-reporting';
import isPublicFolderComposeViewState from 'owa-mail-compose-store/lib/utils/isPublicFolderComposeViewState';
import { learningToolsCapability } from 'owa-capabilities-definitions/lib/learningToolsCapability';
import { populateAddinsCapability } from 'owa-capabilities-definitions/lib/populateAddinsCapability';
import { shouldHideRibbonControlBasedOnHostApp } from './hostAppConfig';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import type TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import { getItem } from './getItem';
import { isItemInFolder } from './isItemInFolder';
import { getComposeViewState } from 'owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState';
import isMessageRecallEnabled from 'owa-cloud-message-recall/lib/utils/isMessageRecallEnabled';
import { isReadingPaneStackedView } from 'owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils';
import isSharedMailboxExplicitLogon from 'owa-mail-store/lib/utils/isSharedMailboxExplicitLogon';
import { isSpamAddinInstalled } from 'owa-addins-store/lib/utils/isSpamAddinInstalled';
import { getExtensionForAddinId } from 'owa-addins-store/lib/store/getExtensionForAddinId';
import getTabById from 'owa-tab-store/lib/utils/getTabById';
import { TabType } from 'owa-tab-store';
import { isTeamsChatEnabledWithDefault } from 'owa-teams-integration/lib/utils/isTeamsChatEnabledWithDefault';
import { isLocalDataBackedMailboxInfo } from 'owa-account-source-list/lib/utils/isLocalDataBackedAccount';
import { isMessageFileMailboxInfo } from 'owa-client-types/lib/MessageFileMailboxInfo';
import { editorCapability } from 'owa-capabilities-definitions/lib/editorCapability';
import getIsLightRPOn from 'owa-light-reading-pane-store/lib/selectors/getIsLightRPOn';
import { hasPaidAccount } from 'owa-has-enterprise-or-premium-license-store/lib/selectors/hasPaidAccount';
import { getAppliedRefiners, getSuggestedRefiners } from 'owa-search-refiners';
import { SearchScenarioId } from 'owa-search-store';
import { mailTemplatesCapability } from 'owa-capabilities-definitions/lib/mailTemplatesCapability';
import { quickPartsCapability } from 'owa-capabilities-definitions/lib/quickPartsCapability';
import { saveEmailAsEMLToFileCapability } from 'owa-capabilities-definitions/lib/saveEmailAsEMLToFileCapability';
import { saveEmailAsMSGToFileCapability } from 'owa-capabilities-definitions/lib/saveEmailAsMSGToFileCapability';
import { saveEmailAsOFTToFileCapability } from 'owa-capabilities-definitions/lib/saveEmailAsOFTToFileCapability';
import getIsTeamsChatTabEnabled from 'owa-tabbed-search/lib/selectors/getIsTeamsChatTabEnabled';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { SearchProvider } from 'owa-search-service/lib/data/schema/SearchProvider';
import { type TableQueryType } from 'owa-mail-list-store';
import type { SearchTableQuery } from 'owa-mail-list-search';
import getSearchProvider from 'owa-mail-search/lib/selectors/getSearchProvider';
import { shareToTeamsGUIDs } from 'owa-addins-surface-actions/lib/utils/addinGUIDConst';

const sentItemsFolderId = 'sentitems';

/**
 * This function decides whether a control should show or not.
 * For any logic on show/hiding controls, please put them here.
 *
 * @controlId the current control being tested to see whether it should show
 * @props Optional parameter, used for certain buttons to test whether they should show.
 *        Note: Props is only undefined in Customizer cases. Certain buttons use this logic truth
 *        when determining whether they should show.
 * @isCustomizer set this to true if we are in customizer view
 */
export const shouldShowRibbonControl: (
    tabId: RibbonTabId,
    controlId: RibbonControlId,
    props: RibbonControlDefinitionProps,
    isCustomizer: boolean
) => boolean = owaComputedFn(function shouldShowRibbonControl(
    tabId: RibbonTabId,
    controlId: RibbonControlId,
    props: RibbonControlDefinitionProps,
    isCustomizer: boolean
): boolean {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    if (
        getMailMenuItemShouldHideForMailbox(
            getCmdIdFromRibbonId(controlId, mailboxInfo),
            mailboxInfo
        )
    ) {
        return false;
    }
    const isUnifiedInbox = isUnifiedInboxMailboxInfo(mailboxInfo);

    if (shouldHideRibbonControlBasedOnHostApp(controlId)) {
        return false;
    }

    const composeProps = props as ComposeRibbonControlDefProps;
    const isHTML: boolean = composeProps.composeViewState?.bodyType === 'HTML';
    const isFileProjection: boolean = isFileActivationProjection(props);
    const selectedTableView = getSelectedTableView() ?? null;
    const isLightRPOn = getIsLightRPOn();

    const composeViewState = getComposeViewState();

    switch (tabId) {
        case 1:
            switch (controlId) {
                case 508:
                    return !isGlobalFeatureEnabled('grp-inboxExp-ribbon');

                case 663:
                    return isGlobalFeatureEnabled('grp-inboxExp-ribbon') && hasEnterpriseAccount();

                case 529:
                    return shouldShowGetAddins(getIsConsumer(mailboxInfo), 1, mailboxInfo);
                case 7022:
                    return (
                        shouldShowGetM365Apps(mailboxInfo) &&
                        !shouldShowGetAddins(getIsConsumer(mailboxInfo), 1, mailboxInfo)
                    );
                case 7016:
                    return isMessageExtensionsFlyoutEnabled(mailboxInfo);

                case 550:
                    return (
                        (isPaid(mailboxInfo) || hasPaidAccount()) &&
                        mailboxInfo.type != 'GroupMailbox'
                    );

                case 657:
                    // Hide the native reporting button if the user has the spam report addin
                    return (
                        !hasSpamReportAddin(mailboxInfo) && isNativeReportingEnabled(mailboxInfo)
                    );

                // Only show these controls if the user has at least one enterprise account
                case 556:
                case 557:
                case 555:
                case 651:
                    return hasEnterpriseAccount();

                case 553:
                    return hasEnterpriseAccount() && !shouldShowReplyByMeetingFlyout(mailboxInfo);

                case 698:
                case 699:
                case 700:
                    return shouldShowReplyByMeetingFlyout(mailboxInfo);

                case 696:
                    return (
                        hasEnterpriseAccount() &&
                        !!isCopilotFeatureEnabled('ReplyByMeeting', mailboxInfo) &&
                        !shouldShowReplyByMeetingFlyout(mailboxInfo)
                    );

                case 678:
                    return !hasEnterpriseAccount();

                // MenuItemType Ribbon_Restore controls whether the restore buttons should be show in the ribbon
                // there are Restore and RestoreAll types as well, which are used to determine if the buttons are enabled
                case 683:
                    return getMailMenuItemShouldShow(81, mailboxInfo);
                case 684:
                    return getMailMenuItemShouldShow(81, mailboxInfo);
                case 558:
                    return !(isUnifiedInbox || mailboxInfo.type === 'GroupMailbox');
                case 686:
                    // Show recall message button in Home Tab when the feature is enabled and:
                    // 1. List View is not in Conversation Mode
                    // 2. List View is in Conversation mode and Reading Pane Conversation is disabled
                    return (
                        selectedTableView &&
                        !isReadingPaneStackedView(selectedTableView?.tableQuery?.listViewType) &&
                        getMailMenuItemShouldShow(83, mailboxInfo)
                    );
                case 697:
                    return (
                        selectedTableView &&
                        !isReadingPaneStackedView(selectedTableView?.tableQuery?.listViewType) &&
                        getMailMenuItemShouldShow(86, mailboxInfo)
                    );

                case 731:
                case 702:
                case 703:
                    return (
                        isGlobalFeatureEnabled('mon-teams-ChatAroundEmail') &&
                        isTeamsChatEnabledWithDefault(mailboxInfo)
                    );

                case 709:
                    return shouldShowShareToTeamsChat(mailboxInfo);
                case 712:
                    return (
                        selectedTableView &&
                        !isReadingPaneStackedView(selectedTableView?.tableQuery?.listViewType) &&
                        getMailMenuItemShouldShow(92, mailboxInfo)
                    );
                case 548:
                    return getMailMenuItemShouldShow(58, mailboxInfo);

                default:
                    return true;
            }

        case 5:
            switch (controlId) {
                case 620:
                    return shouldShowAttachFile(props, isCustomizer);
                case 529:
                    return shouldShowGetAddins(getIsConsumer(mailboxInfo), 5, mailboxInfo);
                case 7016:
                    return shouldShowGetMessageExtensions(mailboxInfo);
                case 7021:
                    return shouldShowPolls(mailboxInfo);
                case 7000:
                    return showShouldLoopComponent(mailboxInfo);
                case 7011:
                    return shouldShowFindTimeControl(getIsConsumer(mailboxInfo));
                case 7026:
                    return isFluidEnabledForSource(
                        'MailCompose',
                        mailboxInfo,
                        LoopType.VideoRecord
                    );
                case 639:
                    return !composeProps.composeViewState.contentPicker.isFeatureEnabled;
                case 7042:
                    return composeProps.composeViewState.contentPicker.isFeatureEnabled;
                case 7054:
                    return isCapabilityEnabled(quickPartsCapability, mailboxInfo);
                default:
                    return true;
            }

        case 4:
            switch (controlId) {
                case 6011:
                    return !isHTML && !isBrowserFirefox();
                case 6012:
                    return isHTML && !isBrowserFirefox();
                default:
                    return true;
            }

        case 3:
            switch (controlId) {
                case 532:
                    return isCapabilityEnabled(learningToolsCapability, mailboxInfo);
                case 558:
                    return !(isUnifiedInbox || mailboxInfo.type === 'GroupMailbox');
                case 687:
                    if (!selectedTableView) {
                        return false;
                    }

                    return (
                        !composeViewState &&
                        isReadingPaneStackedView(selectedTableView.tableQuery?.listViewType)
                    );
                case 720:
                    if (!(selectedTableView || composeViewState)) {
                        return false;
                    }

                    return getIsDarkTheme();
                case 701:
                    return !isLocalDataBackedMailboxInfo(mailboxInfo);
                default:
                    return true;
            }

        case 6:
            switch (controlId) {
                default:
                    return true;
            }

        case 7:
            switch (controlId) {
                default:
                    return true;
            }

        case 8:
            switch (controlId) {
                case 519:
                case 505:
                case 527:
                case 509:
                case 535:
                case 547:
                case 658:
                case 559:
                case 616:
                case 552:
                    return !isFileProjection;
                case 540:
                    return !isFileProjection;
                case 548:
                    return getMailMenuItemShouldShow(58, mailboxInfo);
                case 550:
                    return (
                        (isPaid(mailboxInfo) || hasPaidAccount()) &&
                        mailboxInfo.type != 'GroupMailbox'
                    );
                case 657:
                    // Hide the native reporting button if the user has the spam report addin
                    return (
                        !hasSpamReportAddin(mailboxInfo) &&
                        isNativeReportingEnabled(mailboxInfo) &&
                        !isFileProjection
                    );

                case 678:
                    return !hasEnterpriseAccount();

                case 651:
                case 556:
                case 557:
                case 555:
                    return hasEnterpriseAccount();

                case 553:
                    return hasEnterpriseAccount() && !shouldShowReplyByMeetingFlyout(mailboxInfo);

                case 698:
                case 699:
                case 700:
                    return shouldShowReplyByMeetingFlyout(mailboxInfo);

                case 696:
                    return (
                        hasEnterpriseAccount() &&
                        !!isCopilotFeatureEnabled('ReplyByMeeting', mailboxInfo) &&
                        !shouldShowReplyByMeetingFlyout(mailboxInfo)
                    );

                case 686:
                    return (
                        isMessageRecallEnabled(mailboxInfo) &&
                        shouldShowTrackingControlsOnReadTab(props) &&
                        !isFileProjection
                    );

                case 697:
                    return shouldShowTrackingControlsOnReadTab(props) && !isFileProjection;

                case 731:
                case 702:
                case 703:
                    return (
                        isGlobalFeatureEnabled('mon-teams-ChatAroundEmail') &&
                        isTeamsChatEnabledWithDefault(mailboxInfo)
                    );

                case 709:
                    return shouldShowShareToTeamsChat(mailboxInfo);

                case 705:
                    return isFileProjection;

                case 532:
                    return isCapabilityEnabled(learningToolsCapability, mailboxInfo);

                case 712:
                    return shouldShowTrackingControlsOnReadTab(props);

                case 7016:
                    return isMessageExtensionsFlyoutEnabled(mailboxInfo);

                default:
                    return true;
            }

        case 10:
            switch (controlId) {
                case 529:
                    return shouldShowGetAddins(getIsConsumer(mailboxInfo), 10, mailboxInfo);
                case 7016:
                    return shouldShowGetMessageExtensions(mailboxInfo);
                case 7021:
                    return shouldShowPolls(mailboxInfo);
                case 7023:
                    // TODO (ADO390010): Remove CopilotDrafts ActionControlId from Ribbon.
                    // This now always returns false since the main Command Center flights have graduated.
                    // This is a TODO to keep the feature graduation PR scoped and less bloated.
                    return false;
                case 7000:
                    return showShouldLoopComponent(mailboxInfo);
                case 7011:
                    return shouldShowFindTimeControl(getIsConsumer(mailboxInfo));
                case 564:
                    return shouldShowSensitivity(props);
                case 6011:
                    return (
                        isGlobalFeatureEnabled('cmp-ribbon-reorg') ||
                        (!isHTML && !isBrowserFirefox())
                    );
                case 6012:
                    return isHTML && !isBrowserFirefox();
                case 7010:
                    return !getIsConsumer(mailboxInfo);
                case 7003:
                    return getIsDarkTheme() && !isLightRPOn;
                case 7026:
                    return isFluidEnabledForSource(
                        'MailCompose',
                        mailboxInfo,
                        LoopType.VideoRecord
                    );
                case 7029:
                    return (
                        isGlobalFeatureEnabled('cmp-dictation') &&
                        (isBrowserChrome() || isBrowserEDGECHROMIUM() || isBrowserFirefox())
                    );
                case 7001:
                    return isCapabilitySupported(editorCapability, mailboxInfo);
                case 7040:
                    return shouldShowTemplateControl(mailboxInfo);
                case 7035:
                    return shouldShowSaveAsControl(mailboxInfo);
                case 7049:
                    return (
                        isGlobalFeatureEnabled('mon-recipient-addrecipients') &&
                        isGlobalFeatureEnabled('mon-recipient-addressbookinribbon')
                    );
                case 7050:
                    return isGlobalFeatureEnabled('mon-recipient-checknamesinribbon');

                default:
                    return true;
            }

        case 11:
            switch (controlId) {
                case 7012:
                    return shouldShowEncrypt(props);
                case 564:
                    return shouldShowSensitivity(props);
                case 7010:
                    return !getIsConsumer(mailboxInfo);
                case 7013:
                    return shouldShowSendLater(props);
                case 7015:
                    return sholdShowDeliveryReceipt(props);
                case 7001:
                    return isCapabilitySupported(editorCapability, mailboxInfo);
                case 7040:
                    return shouldShowTemplateControl(mailboxInfo);
                case 7035:
                    return shouldShowSaveAsControl(mailboxInfo);

                default:
                    return true;
            }

        case 12:
            return true;

        case 13:
            switch (controlId) {
                default:
                    return true;
            }

        case 14:
            switch (controlId) {
                case 739:
                    return false;
                case 741:
                    return shouldShowSearchRefiner('HasAttachment');
                case 742:
                    return shouldShowSearchRefiner('IsUnread');
                case 743:
                    return shouldShowSearchRefiner('IsMentioned');
                case 745:
                    return shouldShowSearchRefiner('IsFlagged');
                case 746:
                    return shouldShowSearchRefiner('HighImportance');
                case 747:
                    return shouldShowSearchRefiner('ToMe');
                case 748:
                    return shouldShowSearchRefiner('DatePicker');
                case 749:
                    return (
                        shouldShowSearchRefiner('IncludeChannels') &&
                        !isGlobalFeatureEnabled('sea-deprecateSerpTabs')
                    );
                case 744:
                    return (
                        shouldShowSearchRefiner('IsMentionedInChats') &&
                        !isGlobalFeatureEnabled('sea-deprecateSerpTabs')
                    );
                case 754:
                case 753:
                case 750:
                    return (
                        !isUnifiedInbox &&
                        !isOfflineOrPSTSearch(selectedTableView) &&
                        !isGlobalFeatureEnabled('sea-deprecateSerpTabs')
                    );
                case 751:
                    return (
                        !isOfflineOrPSTSearch(selectedTableView) &&
                        getIsTeamsChatTabEnabled(mailboxInfo) &&
                        !isGlobalFeatureEnabled('sea-deprecateSerpTabs')
                    );
                case 752:
                    return (
                        !isUnifiedInbox &&
                        !isOfflineOrPSTSearch(selectedTableView) &&
                        !isGlobalFeatureEnabled('sea-disable-searchTabs-files') &&
                        !isGlobalFeatureEnabled('sea-deprecateSerpTabs')
                    );
                default:
                    return true;
            }

        default:
            /* eslint-disable-next-line owa-custom-rules/no-error-dynamic-event-names -- (https://aka.ms/OWALintWiki)
             * Error constructor names can only be a string literals.
             *	> Error constructor names can only be a string literals. Use the diagnosticInfo to add custom data. */
            throw new Error('getShouldShowControlsRules.ts: Unknown tab id: ' + tabId);
    }
});

function hasSpamReportAddin(mailboxInfo: MailboxInfo): boolean {
    // Check if the spam addin is installed from extensibility state
    return (
        isGlobalFeatureEnabled('addin-enableIntegratedSpam') && isSpamAddinInstalled(mailboxInfo)
    );
}

function shouldShowFindTimeControl(isConsumerAcct: boolean): boolean {
    return isGlobalFeatureEnabled('cal-native-findtime') && !isConsumerAcct;
}

function shouldShowReplyByMeetingFlyout(mailboxInfo: MailboxInfo): boolean {
    return (
        (hasEnterpriseAccount() || isMessageFileMailboxInfo(mailboxInfo)) &&
        !!isCopilotFeatureEnabled('ReplyByMeeting', mailboxInfo) &&
        isGlobalFeatureEnabled('cal-copilot-schedule-from-email-bizchat')
    );
}

function shouldShowGetAddins(
    isConsumerAcct: boolean,
    ribbonTab: MailRibbonTabId,
    mailboxInfo: MailboxInfo
): boolean {
    const shouldShowAddInButton =
        !isConsumerAcct &&
        isCapabilityEnabled(populateAddinsCapability, mailboxInfo) &&
        isCapabilityEnabled(addinsCapability, mailboxInfo) &&
        !isMessageExtensionsFlyoutEnabled(mailboxInfo) &&
        !isSharedMailboxExplicitLogon(mailboxInfo);

    if (ribbonTab === 1 || ribbonTab === 10 || ribbonTab === 5) {
        return shouldShowAddInButton && !shouldShowGetM365Apps(mailboxInfo);
    }

    return shouldShowAddInButton;
}

function shouldShowGetM365Apps(mailboxInfo?: MailboxInfo) {
    return (
        isMOS3AppServiceAppsInMailAvailable(mailboxInfo) &&
        isGlobalFeatureEnabled('mos-inContextStore')
    );
}

function shouldShowGetMessageExtensions(mailboxInfo?: MailboxInfo): boolean {
    return isMessageExtensionsFlyoutEnabled(mailboxInfo);
}

function shouldShowPolls(mailboxInfo: MailboxInfo): boolean {
    return (
        isGlobalFeatureEnabled('cmp-fluid-chapterThree') &&
        isFluidEnabledForSource('MailCompose', mailboxInfo, LoopType.Poll)
    );
}

function showShouldLoopComponent(mailboxInfo: MailboxInfo): boolean {
    return isFluidEnabledForSource('MailCompose', mailboxInfo, LoopType.Fluid);
}

const shouldShowEncrypt = owaComputedFn(function shouldShowEncrypt(
    props: RibbonControlDefinitionProps
): boolean {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const { RmsV2UIEnabled } = getUserConfiguration(); // indicates whether SimplifiedClientAccessEnabled is true
    return !!(
        RmsV2UIEnabled &&
        isEncryptionEnabled(mailboxInfo) &&
        isEncryptionTemplateAvailable(mailboxInfo)
    );
});

/**
 * Display sensitivity label dropdown only if user is not a consumer. In case of consumer, we hide the sensitivity label button.
 * Future TO-DO: Add license check for mail workload
 *                  and modify return statement to display dropdown list on the basis of license check as well.
 */
const shouldShowSensitivity = owaComputedFn(function shouldShowSensitivity(
    props: RibbonControlDefinitionProps
): boolean {
    return !isConsumer(undefined /* smtpAddress */, getRibbonMailboxInfo(props.mailboxInfo));
});

function shouldShowAttachFile(props: RibbonControlDefinitionProps, isCustomizer: boolean) {
    props = props as ComposeRibbonControlDefProps;
    return (
        isCustomizer ||
        (props.attachmentPickers != undefined && props.composeViewState != undefined)
    );
}

function shouldShowSendLater(props: RibbonControlDefinitionProps): boolean {
    props = props as ComposeRibbonControlDefProps;
    const viewState = props.composeViewState;
    if (viewState) {
        return (
            !!getUserConfiguration().AsyncSendEnabled &&
            !isGroupComposeViewState(viewState) &&
            !isPublicFolderComposeViewState(viewState) &&
            /* eslint-disable-next-line no-restricted-properties  -- (https://aka.ms/OWALintWiki)
             * Adding IsShadowMailbox to restricted properties/methods.
             *	> 'IsShadowMailbox' is restricted from being used. IsCloudCache/IsShadowMailbox should be resolved in ECS as a filter in a feature flight if possible. */
            !getAccountScopeUserSettings(viewState.mailboxInfo).SessionSettings?.IsShadowMailbox
        );
    }
    return false;
}

function sholdShowDeliveryReceipt(props: RibbonControlDefinitionProps): boolean {
    props = props as ComposeRibbonControlDefProps;
    const viewState = props.composeViewState;
    if (viewState) {
        /* eslint-disable-next-line no-restricted-properties  -- (https://aka.ms/OWALintWiki)
         * Adding IsShadowMailbox to restricted properties/methods.
         *	> 'IsShadowMailbox' is restricted from being used. IsCloudCache/IsShadowMailbox should be resolved in ECS as a filter in a feature flight if possible. */
        return !getAccountScopeUserSettings(viewState.mailboxInfo).SessionSettings?.IsShadowMailbox;
    }
    return false;
}

function getIsConsumer(mailboxInfo: MailboxInfo): boolean {
    return isConsumer(undefined /* smtpAddress */, mailboxInfo);
}

function shouldShowTrackingControlsOnReadTab(props: RibbonControlDefinitionProps) {
    const clientItemId = (props as ReadOnlyRibbonControlDefProps).itemId;
    const item = getItem(clientItemId);
    if (item) {
        // Do not show if the item in not in sent items folder or if the item class is a Meeting
        return (
            isItemInFolder(item, [sentItemsFolderId]) &&
            item.ItemClass?.indexOf('IPM.Schedule.Meeting') == -1
        );
    }
    return false;
}

export function shouldShowShareToTeamsChat(mailboxInfo: MailboxInfo): boolean {
    return (
        isGlobalFeatureEnabled('mon-teams-ShareToTeamsChat') &&
        isTeamsChatEnabledWithDefault(mailboxInfo) &&
        (!isGlobalFeatureEnabled('settings-hideDisabledShareToTeams') ||
            isShareToTeamsChatEnabledByAdmin(mailboxInfo))
    );
}

function isFileActivationProjection(props: RibbonControlDefinitionProps): boolean {
    const ribbonProps = props as ReadOnlyRibbonControlDefProps;

    if (
        isGlobalFeatureEnabled('mon-msg-local-parser') &&
        isMessageFileMailboxInfo(ribbonProps.itemId?.mailboxInfo)
    ) {
        return true;
    }

    if (!isGlobalFeatureEnabled('mon-file-eml-msg')) {
        return false;
    }

    const tab = getTabById(ribbonProps.projectionTabId);

    if (tab?.type === TabType.SecondaryReadingPane) {
        // check if file projection
        return !!tab.data?.isFileActivation;
    }

    return false;
}

function shouldShowSearchRefiner(refinerName: string): boolean {
    const suggestedRefiners = getSuggestedRefiners(SearchScenarioId.Mail);
    const suggestedRefiner = suggestedRefiners.find(refiner => refiner.name === refinerName);

    const appliedRefiners = getAppliedRefiners(SearchScenarioId.Mail);
    const appliedRefiner = appliedRefiners.find(refiner => refiner.name === refinerName);

    return !!(suggestedRefiner || appliedRefiner);
}

function shouldShowTemplateControl(mailboxInfo: MailboxInfo): boolean {
    return isCapabilityEnabled(mailTemplatesCapability, mailboxInfo);
}

/**
 * The Save As ribbon button should be shown only if at least one of the
 * file-format options that can appear in its flyout menu is enabled. The
 * set of options must be kept in sync with `getSaveAsFileSections` in the
 * `owa-mail-compose-control-sections` package.
 */
export function shouldShowSaveAsControl(mailboxInfo: MailboxInfo): boolean {
    return (
        isCapabilityEnabled(saveEmailAsEMLToFileCapability, mailboxInfo) ||
        isCapabilityEnabled(saveEmailAsMSGToFileCapability, mailboxInfo) ||
        isCapabilityEnabled(saveEmailAsOFTToFileCapability, mailboxInfo)
    );
}

function isOfflineOrPSTSearch(selectedTableView: TableView): boolean {
    if (!selectedTableView || selectedTableView.tableQuery.type !== 1) {
        return false;
    }

    const searchTableQuery = selectedTableView.tableQuery as SearchTableQuery;
    const searchProvider = getSearchProvider(searchTableQuery.searchScope);

    return searchProvider === SearchProvider.Offline || searchProvider === SearchProvider.PST;
}

export function isShareToTeamsChatEnabledByAdmin(mailboxInfo: MailboxInfo): boolean {
    const shareToTeamsAppId = shareToTeamsGUIDs[0];
    const extension = getExtensionForAddinId(shareToTeamsAppId, mailboxInfo);
    return extension?.Enabled === true;
}
